"use client"

import type * as React from "react"

import { toast as sonnerToast } from "@owlcat/ui"

// Sonner-backed compat layer.
//
// Portal historically used a radix-toast `useToast()` with the call shape
// `toast({ title, description, variant })`. The toast ENGINE is now @owlcat/ui's
// sonner `Toaster` (mounted once in the root layout). This shim preserves the
// old call surface so every existing `useToast()` / `toast({...})` call site is
// unchanged, mapping the `variant` onto sonner's severity helpers
// (destructive → error, success/warning/info → matching helpers, default → base).

type ToastVariant = "default" | "destructive" | "success" | "warning" | "info"

export interface ToastInput {
  title?: React.ReactNode
  description?: React.ReactNode
  variant?: ToastVariant
  /** milliseconds the toast stays visible (sonner `duration`) */
  duration?: number
}

function fire({ title, description, variant, duration }: ToastInput) {
  // Prefer the title as sonner's primary message; fall back to the description
  // when only a description is supplied (avoids an empty heading + body).
  const message = title ?? description
  const options =
    title != null && description != null
      ? { description, duration }
      : { duration }

  switch (variant) {
    case "destructive":
      return sonnerToast.error(message, options)
    case "success":
      return sonnerToast.success(message, options)
    case "warning":
      return sonnerToast.warning(message, options)
    case "info":
      return sonnerToast.info(message, options)
    default:
      return sonnerToast(message, options)
  }
}

function toast(input: ToastInput) {
  const id = fire(input)
  return {
    id,
    dismiss: () => sonnerToast.dismiss(id),
    update: (next: ToastInput) => {
      sonnerToast.dismiss(id)
      return fire(next)
    },
  }
}

function useToast() {
  return {
    toast,
    dismiss: (toastId?: string | number) => sonnerToast.dismiss(toastId),
  }
}

export { useToast }
